import { Timestamp } from 'firebase/firestore';
import { RRule } from 'rrule';

// Utils
export type Optional<T> = T | null | undefined;
export type Nullable<T> = T | null;
export type NamedEntity = {
    id: string;
    name: string;
};

export enum CalculationSetups {
    ZERO_BASED = 'ZERO_BASED',
    CARRY_OVER = 'CARRY_OVER',
}

export enum RecommendationCategory {
    BANK_SERVICES = 'BANK_SERVICES',
    CREDIT_CARDS = 'CREDIT_CARDS',
    LOANS = 'LOANS',
    INSURANCE = 'INSURANCE',
    INVESTMENTS = 'INVESTMENTS',
    BUSINESS = 'BUSINESS',
    CASHBACK = 'CASHBACK',
    CASH_REWARDS = 'CASH_REWARDS',
    MORTGAGES = 'MORTGAGES',
    TAX_SAVINGS = 'TAX_SAVINGS',
    TRAVEL_PERKS = 'TRAVEL_PERKS',
    EDUCATION = 'EDUCATION',
    UTILITIES = 'UTILITIES',
    SUBSCRIPTIONS = 'SUBSCRIPTIONS',
}

export interface Recommendation {
    id: string;
    title: string;
    description: string;
    category: RecommendationCategory;
    provider: string; // e.g., "Chase", "Sofi"
    providerLogoUrl?: string; // Optional URL to the provider's logo
    actionUrl: string; // Link to apply/learn more
    matchReason: string; // E.g., "Because you have a high cash balance..."
    potentialValue?: string; // E.g., "Up to 5% APY", "Save $50/mo"
    isFeatured?: boolean; // Highlight top recommendations
}

export type RuleOperator = 'gt' | 'lt' | 'eq' | 'neq' | 'contains' | 'not_contains' | 'exists' | 'not_exists';

export interface OfferRule {
    field: string; // e.g., "totalCash", "totalDebt", "hasInvestmentAccounts"
    operator: RuleOperator;
    value: any;
}

export interface OfferTemplate {
    id: string;
    title: string;
    description: string;
    category: RecommendationCategory;
    provider: string;
    providerLogoUrl?: string;
    actionUrl: string;
    matchReasonTemplate: string; // e.g., "Because you have {totalCash} in low-yield accounts"
    potentialValue?: string;
    isFeatured?: boolean;
    isActive: boolean;
    rules: OfferRule[];
    createdAt: Timestamp;
    updatedAt: Timestamp;
}

export enum Plan {
    FREE = 'FREE',
    PREMIUM = 'PREMIUM',
    FOREVER = 'FOREVER',
}

export type UserSubscription = {
    id: string;
    plan: Plan;
    startDate: Timestamp;
    endDate: Timestamp | null;
    isTrial: boolean;
    trialEndDate: Timestamp | null;
    userId: string;

    createdAt: Timestamp;
    updatedAt: Timestamp;
    isComplimentary?: boolean;
};

export type TelegramChatDetails = {
    id: string;
    chatId: string;
    userId: string;
    createdAt: Timestamp;
    updatedAt: Timestamp;
};

export enum Role {
    ADMIN = 'ADMIN',
    EDITOR = 'EDITOR',
    VIEWER = 'VIEWER',
}

export enum CollaborationMode {
    HOUSEHOLD = 'HOUSEHOLD',
    PARTNERSHIP = 'PARTNERSHIP',
    FAMILY = 'FAMILY',
}

export enum PermissionAccessLevel {
    READ = 'READ',
    WRITE = 'WRITE',
    NONE = 'NONE',
}

export enum PermissionResourceType {
    TRANSACTION = 'TRANSACTION',
    GOAL = 'GOAL',
    ACCOUNT = 'ACCOUNT',
    CATEGORY = 'CATEGORY',
    BUDGET = 'BUDGET',
}

export interface PermissionRule {
    id: string;
    resourceType: PermissionResourceType;
    resourceId?: string;
    categoryId?: string;
    amountLimit?: number;
    accessLevel: PermissionAccessLevel;
    grantedToUserId: string;
    expiresAt?: number;
}

export type HouseholdMember = {
    userId: string;
    role: Role;
    collaborationRole?: 'PARENT' | 'CHILD' | 'PARTNER';
    permissions?: PermissionRule[];
    joinedAt: Timestamp;
    status: 'INVITED' | 'ACTIVE';
    email?: string; // For invitations
};

export type Household = {
    id: string;
    name: string;
    ownerId: string;
    members: HouseholdMember[];
    currency: string;

    mode: CollaborationMode;
    settings?: {
        splitRatio?: { [userId: string]: number };
        autoShareCategories?: string[];
    };

    createdAt: Timestamp;
    updatedAt: Timestamp;
};

export type SerializableUserSubscription = {
    id: string;
    plan: Plan;
    startDate: number;
    endDate: number | null;
    isTrial: boolean;
    trialEndDate: number | null;
    userId: string;
    createdAt: number;
    updatedAt: number;
};

export type User = {
    id: string;
    email: string;
    fullName: string;
    photoURL?: string;
    householdId?: string;
    accountBalance?: number;
    userTier?: Plan;
    isAdmin?: boolean;
    referralRewardOverride?: number;
    isInfluencer?: boolean;
    emailVerified?: boolean;
    phoneVerified?: boolean;
    phoneNumber?: string;
    mfaEnabled?: boolean;
    authProvider?: string; // e.g., 'yandex.ru', 'apple.com', 'google.com'
};

export type ImpersonationSession = {
    id: string;
    adminId: string;
    adminEmail: string | null;
    targetUserId: string;
    targetEmail: string | null;
    startedAt: Timestamp;
};

export type PricingConfig = {
    PREMIUM: {
        monthly: number;
        yearly: number;
    };
    FOREVER: {
        price: number;
    };
};

export type AppSettings = {
    referralRewardAmount: number;
    pricing?: PricingConfig;
    lastUpdated?: Timestamp;
    updatedBy?: string;
};

export type PaymentMethod = {
    id: string;
    userId: string;
    rebillId: string;
    pan: string; // Masked PAN (e.g. 4300*******0777)
    expDate: string; // e.g. 1122
    isDefault: boolean;
    createdAt: Timestamp;
    updatedAt: Timestamp;
};

export type MinimalAccount = {
    id: string;
    name: string;
    number?: string;
    type: AccountType;
    subType?: string;
    // Optional properties for compatibility with Account type
    startingBalance?: number;
    adjustments?: number;
    lastChecked?: number;
    createdAt?: number;
    updatedAt?: number;
    householdId?: string;
};

export type UserSetting = {
    currency: string;
    defaultTaxRate: number;
    startingDate: string;
    calculationSetup: CalculationSetups;
    method?: string;
    region?: string;
    preferences?: {
        defaultTheme: 'dark' | 'light' | 'system';
        enableShortcuts: boolean;
        hideSidebarByDefault: boolean;
        showAds: boolean;
        enableBudgetAlerts?: boolean;
        appMode?: 'basic' | 'full';
    };
};

export type FinancialCategory = 'income' | 'bills' | 'expenses' | 'savings' | 'subscriptions' | 'debts';

export type Spender = NamedEntity;
export type Income = NamedEntity;
export type Bill = NamedEntity;
export type Expense = NamedEntity;
export type Saving = NamedEntity;
export type Subscription = NamedEntity;
export type Debt = NamedEntity;

export type BufferBudgetRecord = {
    id: string;
    income: number;
    date: string;
    createdAt: Timestamp;
    updatedAt: Timestamp;
};

export type InformalDebtRecord = {
    id: string;
    lenderOrBorrower: string;
    amount: number;
    loanDate: Timestamp;
    dueDate: Timestamp;
    agreementTerms: string;
    emotionalImpact: string;
    actionPlan: string;
    paidAmount?: number;
    paymentDate?: Timestamp | null;

    userId: string;
    createdAt: Timestamp;
    updatedAt: Timestamp;
};

export type GoalRecord = {
    id: string;
    name: string;
    description: string;
    deadline: Timestamp | null;
    isImportant: boolean;
    isUrgent: boolean;
    actionPlan: string;
    quadrant?: string;
    owners?: string[];
    visibility?: 'PRIVATE' | 'SHARED';
    userId: string;
    createdAt: Timestamp;
    updatedAt: Timestamp;
};

export type HabitRecord = {
    id: string;
    name: string;
    description: string;
    frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
    startDate: Timestamp;
    completedDates: string[];
    userId: string;
    createdAt: Timestamp;
    updatedAt: Timestamp;

    // Smart Features
    automationConfig?: {
        type: HabitAutomationType;
        limitAmount?: number; // For BUDGET_LIMIT
        targetCategoryId?: string; // For BUDGET_LIMIT
        // For SAVINGS_DEPOSIT, we might just check ANY transfer to a 'Savings' type account
    };
};

export enum HabitAutomationType {
    NO_SPEND = 'NO_SPEND',
    BUDGET_LIMIT = 'BUDGET_LIMIT',
    SAVINGS_DEPOSIT = 'SAVINGS_DEPOSIT',
}

export type EmoSpendingRecord = {
    id: string;
    amount: number;
    what: string;
    date: Timestamp;
    feel: 'STRESSED' | 'ANXIOUS' | 'BORED' | 'HAPPY' | 'SAD' | 'ANGRY' | 'EXCITED';
    couldDone: string;
    userId: string;

    createdAt: Timestamp;
    updatedAt: Timestamp;
};

export enum SubscriptionFrequency {
    MONTHLY = 'Every Month',
    SEMI_ANNUALLY = 'Every 6 Months',
    ANNUALLY = 'Every Year',
}

/** @deprecated Use LedgerTransaction or RecurringTransactionRule instead */
export type SubscriptionRecord = {
    id: string;
    name: string;
    amount: number;
    category: string;
    startDate: Timestamp;
    frequency: SubscriptionFrequency;
    paidDates: Timestamp[];
    isActive: boolean;
    createdAt: Timestamp;
    updatedAt: Timestamp;

    // local properties
    rrule?: RRule;
};

export enum OptionalTransactionType {
    EXPENSES = 'EXPENSES',
    SAVINGS = 'SAVINGS',
    INVESTMENTS = 'INVESTMENTS',
    GUILT_FREE = 'GUILT_FREE',
}

export enum TransactionType {
    INCOME = 'INCOME',
    BILLS = 'BILLS',
    SUBSCRIPTIONS = 'SUBSCRIPTIONS',
    DEBTS = 'DEBTS',
    TRANSFERS = 'TRANSFERS',
    EXPENSE = 'EXPENSE', // Generic expense
    INITIAL_BALANCE = 'INITIAL_BALANCE',
    RECONCILIATION = 'RECONCILIATION',
}

export enum BalanceCategory {
    ASSET = 'ASSET',
    LIABILITY = 'LIABILITY',
    EQUITY = 'EQUITY',
}

export type AllTransactionType = TransactionType | OptionalTransactionType;

/** @deprecated Use LedgerTransaction instead */
export type VariableTransactionRecord = {
    id: string;
    date: Timestamp;
    amount: number;
    currency?: string; // ISO currency code (e.g., 'USD', 'EUR')
    subCategoryId: string;
    category: AllTransactionType;
    accountProvenanceId: string | null;
    distribution: Distribution | null;
    spenderOrEarnerId: string | null;
    description: string | null;
    userId: string;
    paid?: boolean;

    // Income only
    isBeforeTax?: boolean | null;
    accountReceivableId: string | null;
    taxRate?: number | null;

    location?: string | null;

    createdAt: Timestamp;
    updatedAt: Timestamp;
};

export enum Distribution {
    NEED = 'NEED',
    WANT = 'WANT',
    SAVINGS = 'SAVINGS',
}

// ==========================================
// NEW UNIFIED LEDGER TYPES (PHASE 5)
// ==========================================

export enum TransactionStatus {
    POSTED = 'POSTED', // Completed transaction (affects balance)
    PENDING = 'PENDING', // Bank pending or manually entered but unchecked
    SCHEDULED = 'SCHEDULED', // Future occurrence based on rule
}

export enum TransactionSource {
    MANUAL = 'MANUAL',
    BANK_IMPORT = 'BANK_IMPORT',
    RECURRING_RULE = 'RECURRING_RULE',
    SYSTEM = 'SYSTEM', // Initial balance, Reconciliations
}

/**
 * Provenance of a Daily Rate or Transaction Money Snapshot.
 */
export type FxRateSource =
    | 'provider_cron'
    | 'provider_lazy'
    | 'fallback_previous_day'
    | 'admin_override'
    | 'import_override'
    | 'backfill'
    | 'user_defined'
    | 'same_currency';

/**
 * Whether a Cross-Currency Transfer leg matches the system-quoted conversion or was edited by the User.
 */
export type FxKind = 'quoted' | 'custom';

export type LedgerTransaction = {
    id: string;
    userId: string;

    // Core financial data
    amount: number; // Always absolute value
    currency: string;
    date: Timestamp;
    type: TransactionType; // INCOME, EXPENSE, TRANSFER

    // Transaction Money Snapshot (server-attached)
    systemAmountUsd?: number;
    fxRate?: number; // original currency -> USD
    fxRateDate?: string; // Moscow Calendar Day (YYYY-MM-DD) of the rate actually used
    fxRateSource?: FxRateSource;
    fxFallbackFromDate?: string;
    fxSnapshotPending?: boolean; // Future-dated when written, so it has no rate yet

    // Categorization
    categoryId: string | null; // Used to be subCategoryId; null when no subcategory was chosen
    categoryName?: string; // Cache for performance
    categoryGroup: AllTransactionType; // BILLS, SUBSCRIPTIONS, DEBTS, EXPENSES, SAVINGS, INVESTMENTS, GUILT_FREE, INCOME…
    distribution?: Distribution | null; // 50/30/20 Rule

    // Account Linkage
    accountId: string; // The account this affects
    transferAccountId?: string; // If TRANSFER, where it went

    // Cross-Currency Transfer
    transferPairId?: string; // Shared by the legs
    transferLeg?: 'debit' | 'credit';
    fxKind?: FxKind; // was user-edited or matches user quotes

    // Description & Metadata
    description: string;
    merchant?: string; // New field for clearer UI
    location?: string;
    spenderOrEarnerId?: string; // Who spent/earned (Member ID)
    labels?: string[]; // Tags/Labels

    // Tax Details (Income mainly)
    isBeforeTax?: boolean | null;
    taxRate?: number | null;

    // Recurring

    // Status & Origin
    status: TransactionStatus;
    source: TransactionSource;

    // Links
    recurringRuleId?: string; // Link to the template that created this
    originalDate?: Timestamp; // For recurringRuleId docs: Keep rule's original occurence date to match even if edited
    importReferenceId?: string; // ID from the bank/CSV

    createdAt: Timestamp;
    updatedAt: Timestamp;
};

/** Base recurrence unit — matches RRule's own FREQ vocabulary 1:1 */
export enum RecurrenceFrequency {
    DAILY = 'DAILY',
    WEEKLY = 'WEEKLY',
    MONTHLY = 'MONTHLY',
    YEARLY = 'YEARLY',
}

export type RecurringTransactionRule = {
    id: string;
    userId: string;

    // Pattern
    frequency: RecurrenceFrequency;
    interval: number; // e.g., every 2 weeks = {WEEKLY, 2}
    startDate: Timestamp;
    endDate?: Timestamp | null;

    // Template for child transactions
    template: Omit<LedgerTransaction, 'id' | 'userId' | 'date' | 'status' | 'source' | 'createdAt' | 'updatedAt'>;

    isActive: boolean;

    createdAt: Timestamp;
    updatedAt: Timestamp;
};

// ==========================================

export enum AccountType {
    CHECKING = 'CHECKING',
    SAVINGS = 'SAVINGS',
    CARD = 'CARD',
    CASH = 'CASH',
    INVESTMENT = 'INVESTMENT',
    LOAN = 'LOAN',
    CRYPTO = 'CRYPTO', // Added CRYPTO type
}

export type CryptoConnectionDetails = {
    exchangeId: string; // e.g., 'binance', 'coinbase'
    apiKey: string;
    // We will NOT store the secret in the frontend state if possible,
    // but for now we might need to store it encrypted or just rely on the user understanding the risk of "Read Only" keys.
    // For this implementation, we will store it in the account record (security tradeoff accepted for MVP).
    apiSecret: string;
    uid?: string; // Some exchanges need a UID or Passphrase
    password?: string; // Some need a password
    isEncrypted?: boolean; // If true, apiKey, apiSecret, and password are encrypted
};

export type AccountSubType = {
    type: AccountType;
    subTypes: string[];
};

export type CoinBalance = {
    free: number;
    used: number;
    total: number;
    valueUsd?: number;
    priceUsd?: number;
};

export type Account = {
    // Common properties
    id: string;
    name: string;
    number?: string;
    type: AccountType;
    subType: string;
    bank?: Bank;
    currency?: string; // ISO currency code (e.g., 'USD', 'EUR')

    /** @deprecated Use transactions SUM instead. This is only for legacy/migration. */
    adjustments?: number;

    /** Cost basis (original purchase value) for investment/crypto accounts, used to compute Profit/Loss on the Investments dashboard. */
    costBasis?: number;

    /** The cached current balance, calculated by Cloud Functions from the ledger. */
    balance?: number;

    lastChecked: number;
    userId: string;
    createdAt: number;
    updatedAt: number;

    recordHash?: string;
    householdId?: string;
    memberIds?: string[];
    owners?: string[];
    visibility?: 'PRIVATE' | 'SHARED';

    cryptoDetails?: CryptoConnectionDetails;
    cachedHoldings?: Record<string, CoinBalance>;

    isTrainingAccount?: boolean;
};

export type AccountTransfer = {
    id: string; // transferPairId for a two-leg transfer
    fromAccountId: string;
    toAccountId: string;
    /** Debit leg, in the source Account Currency. */
    amount: number;
    /** Credit leg, in the destination Account Currency; differs from `amount` across currencies. */
    toAmount?: number;
    fromCurrency?: string;
    toCurrency?: string;
    fxKind?: FxKind;
    legIds?: { debit: string; credit: string };
    date: Timestamp;
    done?: boolean;
    userId: string;
    createdAt: Timestamp;
    updatedAt: Timestamp;
};

export type SubCategoryBudget = {
    id: string;
    subCategoryId: string; // "source" for Income
    budget: number; // "expected" for Income
    category: AllTransactionType;

    yearMonth: string; // "YYYY-MM"
    userId: string;

    // For income only
    payday?: Timestamp | null;
    depositAccountId?: string | null;
    paymentId?: string | null;

    real?: number; // on client only

    createdAt: Timestamp;
    updatedAt: Timestamp;
};

export type ReferralDetails = {
    referralCode: string;
    referredBy?: string | null;
};

export type ReferralRewardStatus = 'PENDING' | 'COMPLETED' | 'CANCELLED';
export type ReferralRewardType = 'CREDIT' | 'UNLOCK' | 'CASH';

export type ReferralReward = {
    id: string;
    referrerId: string;
    refereeId: string;
    rewardType: ReferralRewardType;
    amount: number;
    currency: string;
    status: ReferralRewardStatus;
    createdAt: Timestamp;
    processedAt?: Timestamp | null;
};

export type SpendingAllocation = {
    subCategoryId: string;
    category: string;
    amount: number;

    // optionnal local
    subCategoryName?: string;
};

export type ConsciousSpendingRecord = {
    netWorthGoal: number;
    monthlyIncomeGoal: number;
    spendingAllocations: SpendingAllocation[];
};

export type SpendingPlanMember = {
    id: string;
    isOwner: boolean;
    email: string;
    fullName: string;
};

export type HouseholdSpendingPlanRecord = {
    id: string;
    name: string;
    members: SpendingPlanMember[];
    memberIds: string[];
};

export enum Bank {
    'TBANK' = 'TBank',
    'SBER' = 'Sber',
    'OZON' = 'Ozon Bank',
    'NOT_PRESENT_IN_LIST' = 'NOT_PRESENT_IN_LIST',
}

export type SinkingFund = {
    id: string;
    name: string;
    goal: number;
    monthlyGoal: number;
    start: number;
    userId: string;
    targetDate?: Timestamp | null;
    // Shared Goals
    visibility?: 'PRIVATE' | 'SHARED';
    owners?: string[];
    householdId?: string;
};

export type SinkingFundContribution = {
    id: string;
    fundId: string;
    date: Timestamp;
    amount: number;
    userId: string;
};

// Others
export type FetchState<T> = {
    data: T | null;
    loading: boolean;
    error: string | null;
};

// Budget Recommendations
export type BudgetRecommendationType = 'reduce' | 'increase' | 'reallocate';

export type BudgetRecommendation = {
    id: string;
    type: BudgetRecommendationType;
    subCategoryId: string;
    subCategoryName: string;
    category: AllTransactionType;
    currentAmount: number;
    suggestedAmount: number;
    reason: string;
    potentialSavings?: number;
    confidence: number; // 0-100, based on data consistency
    monthsAnalyzed: number;
};

export type RecommendationSummary = {
    totalPotentialSavings: number;
    recommendationCount: number;
    recommendations: BudgetRecommendation[];
};

export type SelectOption = {
    label: string;
    value: any;
};

export type CurrencyData = {
    code: string;
    name: string;
    symbol: string;
    formatMoney: (amount: number, convert?: boolean) => string;
};

export type SubscriptionTrackerSettings = {
    reminderTiming: number | null;
    notifyByTelegram: boolean;
    notifyByEmail: boolean;
    watchedSubscriptionIds: string[];
};

// Recurring Pattern Detection
export type RecurringPattern = {
    id: string;
    description: string;
    averageAmount: number;
    amountVariance: number; // Standard deviation
    frequency: RecurrenceFrequency;
    interval: number;
    subCategoryId: string;
    category: AllTransactionType;
    accountId: string | null;
    spenderId: string | null;
    transactions: LedgerTransaction[];
    confidenceScore: number; // 0-100
    suggestedStartDate: Timestamp;
    nextExpectedDate: Timestamp;
    detectedAt: Timestamp;
};

export type PatternDetectionOptions = {
    minOccurrences?: number; // default: 3
    timeWindowMonths?: number; // default: 6
    amountTolerance?: number; // default: 0.1 (10%)
    dateTolerance?: number; // default: 3 days
    minConfidence?: number; // default: 70
};

export type PatternGroup = {
    description: string;
    transactions: LedgerTransaction[];
    averageAmount: number;
    amountVariance: number;
};

// Investments
export enum AssetType {
    STOCK = 'STOCK',
    ETF = 'ETF',
    BONDS = 'BONDS',
    CRYPTO = 'CRYPTO',
    REAL_ESTATE = 'REAL_ESTATE',
    OTHER = 'OTHER',
}

export type InvestmentAsset = {
    id: string;
    userId: string;
    accountId?: string; // Link to a "funding" account if needed
    name: string;
    ticker?: string; // e.g., AAPL, BTC
    type: AssetType;
    quantity: number;
    avgBuyPrice: number;
    currentPrice: number; // Updated manually or via API
    currency: string;

    // For Crypto, we might link to an external ID or keep it separate
    // For MVP, manual assets are stored here.

    createdAt: number;
    updatedAt: number;
};

export type ForecastCategoryBreakdown = {
    categoryId: string;
    categoryName: string; // "Income" or subcategory name
    projectedAmount: number;
    transactionCount: number;
    color?: string;
};

export type ForecastSnapshot = {
    id: string;
    userId: string;
    yearMonth: string; // YYYY-MM

    // Projections (Available at start of month)
    projectedEndBalance: number;
    projectedTotalIncome: number;
    projectedTotalExpenses: number;
    categoryBreakdown: { categoryId: string; categoryName: string; amount: number }[];

    // Actuals (Updated after month ends)
    actualEndBalance?: number;
    actualTotalIncome?: number;
    actualTotalExpenses?: number;

    accuracyScore?: number; // 0-100

    createdAt: Timestamp;
    updatedAt: Timestamp;
};

export type PortfolioSnapshot = {
    id: string; // Format: YYYY-MM-DD
    userId: string;
    date: string; // YYYY-MM-DD
    totalValue: number;
    totalCost: number;
    dayChange: number; // Value - Cost (for that day) or specific daily change if we track it

    // Store as Timestamps in DB, numbers in app
    timestamp: number;
};

export type Order = {
    id: string;
    userId: string;
    plan: Plan;
    isTrial: boolean;
    startDate: Timestamp;
    endDate: Timestamp | null;
    orderDate: Timestamp;
    status: 'PENDING' | 'SUCCESS' | 'FAIL';
    amount?: number;
    currency?: string;
};

export type AssetAllocation = {
    category: string; // Cash, Investments, Real Estate, Crypto, etc.
    value: number;
    percentage: number;
    color: string;
};

export type NetWorthSnapshot = {
    id: string; // YYYY-MM-DD
    userId: string;
    date: string;
    totalAssets: number;
    totalLiabilities: number;
    netWorth: number;
    currency: string;
    timestamp: number;

    // Breakdown for detailed analysis if needed
    assetsBreakdown?: {
        cash: number;
        investments: number;
        crypto: number;
        property: number;
        other: number;
    };
    liabilitiesBreakdown?: {
        creditCards: number;
        loans: number;
        other: number;
    };
};

// Cash Flow Forecast Enhancements
export type ForecastScenario = {
    id: string;
    name: string;
    incomeAdjustmentPercent: number; // e.g., -10 means 10% less income
    expenseAdjustmentPercent: number; // e.g., 20 means 20% more expenses
    excludedRecurringIds: string[]; // Recurring transactions to exclude from forecast
    isActive: boolean;
    createdAt: Timestamp;
};

export type ManualForecastTransaction = {
    id: string;
    date: Timestamp;
    amount: number;
    type: 'INCOME' | 'EXPENSE';
    description: string;
    categoryId?: string;
    categoryName?: string;
    recurrence?: RecurrenceFrequency;
    userId: string;
    scenarioId?: string; // If null, applies to base forecast
    createdAt: Timestamp;
};

// ─── BOAD Finance Integration ────────────────────────────────────────────────

export interface BoadFinancePocket {
    currency: string;
    balance: number;
    frozen_balance?: number;
}

export interface BoadFinanceWallet {
    user_id: string;
    balance: number;
    currency: string;
    pockets: BoadFinancePocket[];
    created_at: string | null;
    updated_at: string | null;
}

/** Stored in localStorage to persist the active connection across sessions. */
export interface BoadFinanceConnection {
    email: string;
    isActive: boolean;
    connectedAt: string; // ISO timestamp
}
